import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import { DEFAULT_WORKFLOW_TIMEZONE, WAIT_UNIT_OPTIONS, WORKFLOW_TIMEZONE_OPTIONS } from './config-options';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { DealApiService } from '../../../../../../deals/services/deal-api.service';
import { DealCycle, DealResponse } from '../../../../../../deals/models/deal.model';
import { AuthService } from '../../../../../../auth-pages/services/auth.service';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildPropertyOptions } from '../../../models/workflow-variable-catalog';

/**
 * Único pipeline lógico de negocios que admite el backend
 * (`WorkflowPipelineActionService.SINGLE_PIPELINE_ID`). Cualquier otro id se rechaza al ejecutar.
 */
const SINGLE_DEAL_PIPELINE_ID = 1;

/**
 * ACTION → Pipeline de ventas: CHANGE_DEAL_STAGE, UPDATE_DEAL_CLOSE_DATE y CHANGE_PIPELINE.
 *
 * Las acciones sobre tickets tienen su propio panel ({@code ticket-action-config}): antes caían
 * aquí y este solo sabía mostrar un aviso de "pendiente", sin un solo campo de ticket.
 */
@Component({
  selector: 'app-wf-pipeline-action-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective, EntitySelectComponent, WorkflowPropertySelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('CHANGE_DEAL_STAGE') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="dealSource" />
          <div>
            <label [class]="labelClass">Etapa destino (ciclo)</label>
            <select [class]="fieldClass" [value]="str('targetStageId')" [disabled]="readOnly()"
                    (change)="patchConfig({ targetStageId: numberOrNull(inputValue($event)) })">
              <option value="">Elige una etapa…</option>
              @for (c of dealCycles(); track c.id) { <option [value]="c.id">{{ c.name }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Motivo (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('reason')" [disabled]="readOnly()"
                   (input)="patchConfig({ reason: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('UPDATE_DEAL_CLOSE_DATE') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="dealSource" />
          <div>
            <label [class]="labelClass">Modo</label>
            <select [class]="fieldClass" [value]="str('mode', 'RELATIVE')" [disabled]="readOnly()"
                    (change)="patchConfig({ mode: inputValue($event) })">
              <option value="RELATIVE">Relativo (a partir de ahora)</option>
              <option value="FIXED">Fecha fija</option>
              <option value="PROPERTY_BASED">Basado en propiedad</option>
            </select>
          </div>
          @if (str('mode', 'RELATIVE') === 'FIXED') {
            <div>
              <label [class]="labelClass">Fecha</label>
              <input type="date" [class]="fieldClass" [value]="str('date')" [disabled]="readOnly()"
                     (change)="patchConfig({ date: inputValue($event) })" />
            </div>
          } @else if (str('mode', 'RELATIVE') === 'PROPERTY_BASED') {
            <div>
              <label [class]="labelClass">Propiedad de fecha</label>
              <app-wf-property-select
                [value]="str('dateProperty')" [options]="propertyOptions()" [disabled]="readOnly()"
                placeholder="Elige una propiedad…"
                (valueChange)="patchConfig({ dateProperty: $event })"></app-wf-property-select>
            </div>
          } @else {
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label [class]="labelClass">Cantidad</label>
                <input type="number" min="1" [class]="fieldClass" [value]="num('amount', 30)" [disabled]="readOnly()"
                       (change)="patchConfig({ amount: numberValue($event) })" />
              </div>
              <div>
                <label [class]="labelClass">Unidad</label>
                <select [class]="fieldClass" [value]="str('unit', 'DAYS')" [disabled]="readOnly()"
                        (change)="patchConfig({ unit: inputValue($event) })">
                  @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
                </select>
              </div>
            </div>
          }
          <div>
            <label [class]="labelClass">Zona horaria</label>
            <!-- Desplegable y NO texto libre: con un identificador IANA mal escrito la fecha de
                 cierre se calculaba en otra zona (o fallaba) sin avisar. -->
            <select [class]="fieldClass" [value]="str('timezone', defaultTimezone)" [disabled]="readOnly()"
                    (change)="patchConfig({ timezone: inputValue($event) })">
              @for (tz of timezoneOptions; track tz) { <option [value]="tz">{{ tz }}</option> }
              @if (timezoneUnknown(str('timezone'))) {
                <option [value]="str('timezone')">{{ str('timezone') }} (no listada)</option>
              }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Motivo (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('reason')" [disabled]="readOnly()"
                   (input)="patchConfig({ reason: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CHANGE_PIPELINE') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="dealSource" />
          <!--
            El «Pipeline destino (id)» era un número libre y el backend solo acepta el 1
            (validateLogicalPipeline: hoy el CRM tiene un único pipeline lógico de negocios).
            Cualquier otro valor pasaba la validación del builder y fallaba al ejecutarse con
            «El pipeline destino no existe o no pertenece al business».
          -->
          <p class="rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-400">
            Los negocios tienen un único pipeline, así que este paso solo mueve la etapa dentro de él.
          </p>
          <div>
            <label [class]="labelClass">Etapa destino</label>
            <select [class]="fieldClass" [value]="str('targetStageId')" [disabled]="readOnly()"
                    (change)="patchConfig({ targetStageId: numberOrNull(inputValue($event)) })">
              <option value="">(automática)</option>
              @for (c of dealCycles(); track c.id) { <option [value]="c.id">{{ c.name }}</option> }
            </select>
          </div>
          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('preserveStageIfPossible')" [disabled]="readOnly()"
                   (change)="patchConfig({ preserveStageIfPossible: checkboxValue($event) })" />
            Conservar la etapa si es posible
          </label>
          <div>
            <label [class]="labelClass">Motivo (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('reason')" [disabled]="readOnly()"
                   (input)="patchConfig({ reason: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Esta acción de pipeline no tiene campos configurables.
        </p>
      }
    }

    <ng-template #dealSource>
      <div>
        <label [class]="labelClass">Origen del negocio</label>
        <select [class]="fieldClass" [value]="str('dealIdSource', 'CURRENT_RECORD')" [disabled]="readOnly()"
                (change)="patchConfig({ dealIdSource: inputValue($event) })">
          <option value="CURRENT_RECORD">Registro actual</option>
          <option value="EXPLICIT">Negocio específico</option>
        </select>
      </div>
      @if (str('dealIdSource', 'CURRENT_RECORD') === 'EXPLICIT') {
        <div>
          <label [class]="labelClass">Negocio</label>
          <app-entity-select
            [value]="num('dealId') || null" [displayText]="dealName()"
            [searchFn]="dealSearch" [labelFn]="dealLabel" [disabled]="readOnly()"
            placeholder="Buscar negocio por nombre…"
            (valueChange)="patchConfig({ dealId: $event })" (selected)="onDealSelected($event)"></app-entity-select>
        </div>
      }
    </ng-template>

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowPipelineActionConfigComponent extends WorkflowNodeConfigBase implements OnInit {
  private readonly dealApi = inject(DealApiService);
  private readonly auth = inject(AuthService);

  protected readonly unitOptions = WAIT_UNIT_OPTIONS;
  protected readonly timezoneOptions = WORKFLOW_TIMEZONE_OPTIONS;
  protected readonly defaultTimezone = DEFAULT_WORKFLOW_TIMEZONE;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  /** Zona guardada que no está en la lista (viene del JSON crudo): se conserva como opción. */
  protected timezoneUnknown(timezone: string): boolean {
    return !!timezone && !WORKFLOW_TIMEZONE_OPTIONS.includes(timezone);
  }

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  protected readonly dealCycles = signal<DealCycle[]>([]);
  protected readonly dealName = signal('');

  constructor() {
    super();
    // Resuelve el nombre del negocio EXPLICIT al cambiar de nodo (para el displayText del selector).
    effect(() => {
      const id = this.num('dealId');
      if (!id) {
        this.dealName.set('');
        return;
      }
      const businessId = this.auth.getCurrentUser()?.businessId || 1;
      this.dealApi.getDealById(id, businessId).subscribe({
        next: (d) => this.dealName.set(d.name ?? ''),
        error: () => {}
      });
    });
    // `targetPipelineId` sigue siendo obligatorio para el ejecutor aunque ya no se pida en el
    // formulario (el CRM tiene un único pipeline lógico). Se garantiza aquí para que un nodo
    // antiguo sin la clave no muera al ejecutarse.
    effect(() => {
      if (this.readOnly() || this.subtype() !== 'CHANGE_PIPELINE') {
        return;
      }
      if (this.config()['targetPipelineId'] == null) {
        this.patchConfig({ targetPipelineId: SINGLE_DEAL_PIPELINE_ID });
      }
    });
  }

  ngOnInit(): void {
    this.dealApi.getDealCycles().subscribe({
      next: (cycles) => this.dealCycles.set(cycles),
      error: () => {}
    });
  }

  protected numberOrNull(value: string): number | null {
    const n = Number(value);
    return value.trim() && Number.isFinite(n) ? n : null;
  }

  protected onDealSelected(deal: DealResponse | null): void {
    this.dealName.set(deal?.name ?? '');
  }

  dealSearch = (term: string, page: number): Observable<EntitySelectPage<DealResponse>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    return this.dealApi.searchDeals({ businessId, searchName: term || undefined, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as DealResponse[], totalPages: 0 }))
    );
  };
  dealLabel = (d: DealResponse): string => d.name ?? `Negocio #${d.id}`;
}
